// Settings hub shell — left-rail category nav + content outlet.
//
// Pattern follows Zoho's Setup hub: one entry in the sidebar ("Settings"),
// every config page becomes a sub-route under /settings/*. Each section
// renders its own page header inside the content area; the rail just lists
// destinations grouped by domain.
//
// Visibility: each section declares its own role / permission gate (mirrors
// what the route-level RoleRoute enforces). A user only sees rail items they
// can actually open — keeps the rail honest and avoids dead-end 403 redirects.

import React, { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Input } from './ui/input';
import {
    Settings as SettingsIcon, Building2, Calculator, Wallet,
    FileText, ShieldCheck, GitBranch, Tags, Calendar, Coins, ListChecks,
    Landmark, HeartPulse, Search, Clock4, Smartphone,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';

// Per-group icon accent. Active state always lands on indigo (matches the
// app sidebar) — the per-group tint only differentiates idle icons so the
// eye can scan by colour without losing the consistent active treatment.
const GROUP_ACCENT = {
    'Organization': 'text-slate-500',
    'Payroll': 'text-emerald-500',
    'Time & Leave': 'text-amber-500',
    'Mobile App': 'text-pink-500',
    'People': 'text-sky-500',
    'Access & Workflows': 'text-violet-500',
};

// Single source of truth for what shows up in the rail AND what's allowed.
// Keep this in sync with the matching <Route> entries in App.js. Each item
// passes IF (no gate) OR (user role matches) OR (user has any listed perm).
const SECTIONS = [
    {
        group: 'Organization',
        items: [
            {
                to: '/settings', end: true,
                label: 'Company Profile', icon: Building2,
                description: 'Name, GST, address, IDs, logo, bank',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
        ],
    },
    {
        group: 'Payroll',
        items: [
            {
                to: '/settings/payroll-calc',
                label: 'Payroll Calculation', icon: Calculator,
                description: 'Pay basis, weekly off, TDS, gratuity',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
            {
                to: '/settings/pf',
                label: 'Provident Fund (PF)', icon: Landmark,
                description: 'EPFO rate & cap',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
            {
                to: '/settings/esi',
                label: 'Employee State Insurance', icon: HeartPulse,
                description: 'ESIC rate & gross limit',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
            {
                to: '/settings/salary-components',
                label: 'Salary Components', icon: Coins,
                description: 'Custom earnings & deductions',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
                requiredPermissions: ['salary.components.view'],
            },
            {
                to: '/settings/payslip-template',
                label: 'Payslip Template', icon: FileText,
                description: 'Pick layout & brand colour',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
            {
                to: '/settings/payouts',
                label: 'Payouts (RazorpayX)', icon: Wallet,
                description: 'Direct salary disbursal',
                allowedRoles: ['super_admin', 'company_admin'],
            },
            {
                to: '/settings/whatsapp',
                label: 'WhatsApp', icon: Wallet,
                description: 'Meta Cloud API for HR notifications',
                allowedRoles: ['super_admin', 'company_admin'],
            },
        ],
    },
    {
        group: 'Time & Leave',
        items: [
            {
                to: '/settings/leave-types',
                label: 'Leave Types', icon: Calendar,
                description: 'Annual, sick, casual quotas',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
                requiredPermissions: ['leaves.types.manage'],
            },
            {
                to: '/settings/attendance',
                label: 'Attendance & Access', icon: Clock4,
                description: 'Lateness, geo-fence, IP, optional holidays, FBP',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
        ],
    },
    {
        group: 'Mobile App',
        items: [
            {
                to: '/settings/mobile-app',
                label: 'Modules & Features', icon: Smartphone,
                description: 'Show/hide modules in the employee mobile app',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
            },
        ],
    },
    {
        group: 'People',
        items: [
            {
                to: '/settings/departments',
                label: 'Departments & Designations', icon: Tags,
                description: 'Org structure lookups',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
                requiredPermissions: ['lookups.view'],
            },
            {
                to: '/settings/checklists',
                label: 'Onboarding & Exit', icon: ListChecks,
                description: 'Checklist templates',
                allowedRoles: ['super_admin', 'company_admin', 'hr'],
                requiredPermissions: ['checklists.view', 'checklists.manage'],
            },
        ],
    },
    {
        group: 'Access & Workflows',
        items: [
            {
                to: '/settings/roles',
                label: 'Roles & Permissions', icon: ShieldCheck,
                description: 'Custom roles, 60+ permissions',
                allowedRoles: ['super_admin', 'company_admin'],
                requiredPermissions: ['roles.view'],
            },
            {
                to: '/settings/approval-chains',
                label: 'Approval Workflows', icon: GitBranch,
                description: 'Multi-step approver chains',
                allowedRoles: ['super_admin', 'company_admin'],
                requiredPermissions: ['approval_chains.manage'],
            },
        ],
    },
];

// Map a sidebar entry to a stable test id: nav-settings-roles, etc.
const railTestId = (to) => `settings-nav-${to.replace(/^\/+settings\/?/, '') || 'general'}`;

export default function SettingsLayout() {
    const user = useAuthStore((s) => s.user);
    const location = useLocation();
    const role = user?.role || '';
    const userPerms = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
    const [query, setQuery] = useState('');

    // Trim the rail to what this user can actually open. We mirror the rules
    // RoleRoute uses on each <Route> so the rail can never offer a destination
    // that bounces back to /dashboard with a 403. Then apply the search filter.
    const visibleSections = useMemo(() => {
        const canSee = (item) => {
            const hasPermGate = item.requiredPermissions?.length > 0;
            const hasRoleGate = item.allowedRoles?.length > 0;
            if (!hasPermGate && !hasRoleGate) return true;
            if (hasPermGate && item.requiredPermissions.some((p) => userPerms.has(p))) return true;
            if (hasRoleGate && item.allowedRoles.includes(role)) return true;
            return false;
        };
        const q = query.trim().toLowerCase();
        const matches = (item) => !q
            || item.label.toLowerCase().includes(q)
            || item.description.toLowerCase().includes(q);
        return SECTIONS
            .map((s) => ({ ...s, items: s.items.filter((i) => canSee(i) && matches(i)) }))
            .filter((s) => s.items.length > 0);
    }, [role, userPerms, query]);

    return (
        // Below lg the rail stacks above the content instead of taking a fixed
        // 288px bite out of a ~700px tablet viewport (which left every settings
        // form squeezed into the remainder).
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 animate-in" data-testid="settings-shell">
            {/* ── Left rail ──────────────────────────────────────────────── */}
            <aside className="w-full lg:w-72 lg:flex-shrink-0">
                <div
                    className="lg:sticky lg:top-0 rounded-xl border border-slate-200/70 shadow-sm overflow-hidden"
                    style={{ background: 'linear-gradient(180deg, #ffffff 0%, #fafbff 100%)' }}
                >
                    {/* Header — matches the app sidebar's gradient brand strip
                        so the user feels they're in a sub-shell, not a stray page. */}
                    <div
                        className="flex items-center gap-2.5 px-4 py-3.5 border-b border-slate-200/70"
                        style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 50%, #faf5ff 100%)' }}
                    >
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
                            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' }}
                        >
                            <SettingsIcon size={16} className="text-white" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-bold gradient-text leading-tight" style={{ fontFamily: 'Manrope' }}>
                                Settings
                            </p>
                            <p className="text-[10px] text-slate-500 leading-tight">Configure your workspace</p>
                        </div>
                    </div>

                    {/* Search — kicks in fast for the 12-item rail. */}
                    <div className="px-3 pt-3 pb-2">
                        <div className="relative">
                            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search settings..."
                                className="h-8 pl-7 text-xs bg-white"
                                data-testid="settings-search"
                            />
                        </div>
                    </div>

                    <nav className="px-2 pb-3 space-y-3 max-h-[22rem] lg:max-h-[calc(100dvh-12rem)] overflow-y-auto">
                        {visibleSections.length === 0 && (
                            <p className="px-3 py-6 text-center text-xs text-slate-400">
                                No settings match &ldquo;{query}&rdquo;.
                            </p>
                        )}
                        {visibleSections.map((section) => {
                            const accent = GROUP_ACCENT[section.group] || 'text-slate-500';
                            return (
                                <div key={section.group}>
                                    <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                                        {section.group}
                                    </p>
                                    {/* Two columns once the rail is stacked full-width,
                                        back to a single column in the lg side rail. */}
                                    <div className="space-y-0.5 sm:grid sm:grid-cols-2 sm:gap-x-2 sm:gap-y-0.5 sm:space-y-0 lg:block lg:space-y-0.5">
                                        {section.items.map((item) => {
                                            const Icon = item.icon;
                                            return (
                                                <NavLink
                                                    key={item.to}
                                                    to={item.to}
                                                    end={item.end}
                                                    data-testid={railTestId(item.to)}
                                                    className={({ isActive }) =>
                                                        `group relative flex items-start gap-2.5 pl-3 pr-2 py-2 rounded-lg transition-all duration-150 ${
                                                            isActive
                                                                ? 'text-indigo-700 font-semibold shadow-sm'
                                                                : 'text-slate-700 hover:text-indigo-700'
                                                        }`
                                                    }
                                                    style={({ isActive }) => isActive
                                                        ? { background: 'linear-gradient(90deg, #eef2ff 0%, #f5f3ff 100%)' }
                                                        : undefined
                                                    }
                                                >
                                                    {({ isActive }) => (
                                                        <>
                                                            {/* Left accent bar on active — matches app sidebar */}
                                                            {isActive && (
                                                                <span
                                                                    className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1 rounded-r-full"
                                                                    style={{ background: 'linear-gradient(180deg, #6366f1 0%, #a855f7 100%)' }}
                                                                />
                                                            )}
                                                            {/* Hover tint background */}
                                                            {!isActive && (
                                                                <span
                                                                    className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    style={{ background: 'linear-gradient(90deg, rgba(238,242,255,0.7) 0%, rgba(245,243,255,0.4) 100%)' }}
                                                                />
                                                            )}
                                                            <span
                                                                className={`relative flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                                                                    isActive
                                                                        ? 'bg-white/80 ring-1 ring-indigo-200'
                                                                        : 'bg-slate-50 ring-1 ring-slate-200/60 group-hover:bg-white group-hover:ring-indigo-200'
                                                                }`}
                                                            >
                                                                <Icon
                                                                    size={14}
                                                                    strokeWidth={1.75}
                                                                    className={isActive ? 'text-indigo-600' : `${accent} group-hover:text-indigo-600`}
                                                                />
                                                            </span>
                                                            <span className="relative min-w-0 flex-1">
                                                                <span className="block text-[13px] leading-snug">{item.label}</span>
                                                                <span className={`block text-[10.5px] leading-tight mt-0.5 ${isActive ? 'text-indigo-500/80 font-normal' : 'text-slate-500'}`}>
                                                                    {item.description}
                                                                </span>
                                                            </span>
                                                        </>
                                                    )}
                                                </NavLink>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </nav>
                </div>
            </aside>

            {/* ── Content outlet ─────────────────────────────────────────── */}
            <div className="flex-1 min-w-0" key={location.pathname /* remount on nav so animate-in re-fires */}>
                <Outlet />
            </div>
        </div>
    );
}
